// The ONE custom-plan story for #4341's "Custom plans" section — a
// person-started (`initiatedBy: 'user'`) epic carrying every shape the
// section's breakdown has to render in one plan: a landed slice, both held
// reasons (`upstream_group_unmerged`, `area_busy`), two slices in flight and
// one dead-tail stall.
//
// A NEW SIBLING MODULE, not an addition to `plan-stories-mocks.ts`: that file
// sits at 292 of the 300-line ceiling `mock-size.test.ts` enforces, so a sixth
// story lands here instead of pushing it over. `plan-stories-mocks.ts`'s own
// `group`/`merged` helpers are private to that module (nothing there is
// exported but `PlanStory` and `STORIES`), so this file repeats their SHAPE
// rather than importing them — the derivation is the part that must not drift,
// not the helper that builds it.
//
// SAME DERIVATION, though: `disposition` and `stallReason` are computed by
// {@link planGroupDisposition}, the identical core `apps/api` classifies with,
// never typed by hand. `plan-stories-mocks.ts`'s header records ten
// hand-typed-summary drift defects a sweep found on PR #3780 — the same
// mistake here would be invisible in exactly the same way.
//
// `plans-mocks.ts` concatenates this story with `plan-stories-mocks.ts`'s
// `STORIES` before deriving the index + detail responses, so `/v1/plans` and
// `/v1/plans/:id` serve it exactly the way they serve every other plan.

import {
  type PlanGroupHold,
  type PlanProjectionGroup,
  type PlanProjectionPullRequest,
  type PlanProjectionTask,
  planGroupDisposition,
} from '@developerz/domain';
import { at } from './mock-session';
import type { PlanStory } from './plan-stories-mocks';

/** The one repo this custom plan's slices live in — a single-repo epic, no cross-repo chips needed. */
const REPO = 'developerz-ai/audit-export';

/** Same idiom as `plan-stories-mocks.ts`'s private `group()` — see this file's header. */
function group(input: {
  groupId: string;
  position: number;
  title: string;
  status: PlanProjectionGroup['status'];
  dependsOn: readonly string[];
  task: PlanProjectionTask | null;
  pullRequest: PlanProjectionPullRequest | null;
  /** Why the slice's task has not started — absent/null for every non-held row. */
  heldOn?: PlanGroupHold | null;
}): PlanProjectionGroup {
  const verdict = planGroupDisposition({
    groupStatus: input.status,
    task:
      input.task === null
        ? null
        : {
            status: input.task.status,
            pullRequestNumber: input.task.pullRequestNumber,
            outcomeKind: 'pr',
            // No slice here settled no work (#4235) — this story exercises held
            // rows and a dead tail, not that separate cell.
            noWorkSettled: false,
            opsActionRefused: false,
          },
    pullRequestState: input.pullRequest?.state ?? null,
  });
  const { heldOn = null, dependsOn, ...rest } = input;
  return {
    ...rest,
    repoFullName: REPO,
    dependsOn: [...dependsOn],
    ...verdict,
    noWork: null,
    heldOn,
  };
}

/** A merged slice and the PR it landed. */
function merged(id: string, position: number, title: string, number: number): PlanProjectionGroup {
  return group({
    groupId: id,
    position,
    title,
    status: 'merged',
    dependsOn: position === 0 ? [] : [`grp_${position}`],
    task: { id: `tsk_custom_${position + 1}`, status: 'merged', pullRequestNumber: number },
    // `mergeCommitSha: null` — matches `plan-stories-mocks.ts`'s own stories
    // (#3702): this fixture exercises plan rendering, not deploy proof.
    pullRequest: {
      number,
      state: 'merged',
      title,
      mergeable: null,
      mergeDecision: 'auto',
      mergeCommitSha: null,
    },
  });
}

/**
 * Six slices, one repo: landed -> held (area_busy) -> held
 * (upstream_group_unmerged) -> in flight -> in flight -> stalled. The two held
 * groups' `dependsOn` is deliberately chosen so the DAG rail reads sensibly —
 * `grp_3` depends on `grp_2`, which has NOT merged, so "waiting on upstream
 * group unmerged" is literally true of this fixture and not just its label.
 */
function customGroups(): PlanProjectionGroup[] {
  return [
    merged('grp_1', 0, 'Ship the CSV exporter for audit_events', 4401),
    group({
      groupId: 'grp_2',
      position: 1,
      title: 'Add the --since range filter',
      status: 'dispatched',
      dependsOn: ['grp_1'],
      task: { id: 'tsk_custom_2', status: 'pending', pullRequestNumber: null },
      pullRequest: null,
      heldOn: { reason: 'area_busy', pullRequestNumber: null },
    }),
    group({
      groupId: 'grp_3',
      position: 2,
      title: 'Stream the export instead of buffering it',
      status: 'dispatched',
      dependsOn: ['grp_2'],
      task: { id: 'tsk_custom_3', status: 'pending', pullRequestNumber: null },
      pullRequest: null,
      heldOn: { reason: 'upstream_group_unmerged', pullRequestNumber: null },
    }),
    group({
      groupId: 'grp_4',
      position: 3,
      title: 'Wire the exporter into the CLI',
      status: 'dispatched',
      dependsOn: ['grp_1'],
      task: { id: 'tsk_custom_4', status: 'in_progress', pullRequestNumber: null },
      pullRequest: null,
    }),
    group({
      groupId: 'grp_5',
      position: 4,
      title: 'Document the export format',
      status: 'dispatched',
      dependsOn: ['grp_4'],
      task: { id: 'tsk_custom_5', status: 'awaiting_ci', pullRequestNumber: 4402 },
      pullRequest: {
        number: 4402,
        state: 'open',
        title: 'Document the export format',
        mergeable: 'mergeable',
        mergeDecision: null,
        mergeCommitSha: null,
      },
    }),
    group({
      groupId: 'grp_6',
      position: 5,
      title: 'Add an export-size guard rail',
      status: 'dispatched',
      dependsOn: ['grp_5'],
      task: { id: 'tsk_custom_6', status: 'failed', pullRequestNumber: null },
      pullRequest: null,
    }),
  ];
}

/**
 * The Custom plans section's own fixture — a second `initiatedBy: 'user'`
 * story alongside `plan_mock01` in `plan-stories-mocks.ts`; that plan is left
 * untouched (`plan.ts` "EXTEND only" scope), so the two co-exist rather than
 * one replacing the other.
 */
export const CUSTOM_PLAN_STORY: PlanStory = {
  planId: 'plan_mock_custom',
  status: 'open',
  rawTaskId: null,
  initiatedBy: 'user',
  // The null-owner case (#4477): person-started but nothing can name who —
  // the common case measured on prod (9 of 54 person-started plans resolve),
  // because most were started by tokens minted before token-binding shipped.
  // `run_mockplan01` in `plan-stories-mocks.ts` is the resolved-owner case.
  owner: null,
  createdIso: at('2026-08-25'),
  updatedIso: at('2026-08-31'),
  groups: customGroups(),
};
